import styled from "@emotion/styled";
import { AppConnection } from "./AppConnection";
import { CircularProgress, Skeleton, Typography } from "@mui/material";
import { AppConnectionCard } from "./AppConnection.styles";
import { RoleAuthorized } from "@shared/components/RoleAuthorized";
import { UserRoles } from "@store/auth.store";
import { formatInternationalPhoneNumber } from "@shared/utils/form-converters";
import { useConnectedAppsLogic } from "./useConnectedAppsLogic";
import { getConnectedSibling } from "./app-connection-helpers";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
`;

const AppsContainer = styled.div<{ size: string }>`
  display: grid;
  grid-template-columns: ${(p) => (p.size === "small" ? "1fr" : "1fr 1fr")};
  align-items: start;
  width: 100%;
  gap: 1.5rem;

  ${(p) => p.theme.media.mobileDown} {
    grid-template-columns: 1fr;
  }
`;

const ErrorMessage = styled.p`
  color: ${(p) => p.theme.colors.error};
`;

const DisabledOverlay = styled.div<{ disabled: boolean }>`
  pointer-events: ${(p) => (p.disabled ? "none" : "auto")};
`;

type Props = {
  size?: "small" | "large";
  highlightAppId?: string;
};

export const ConnectedApps = ({
  size = "large",
  highlightAppId: highlightAppIdProp,
}: Props = {}) => {
  const {
    user,
    connectedApps,
    connectedAppsQuery,
    actionsAppsDisconnectMutation,
    connectionOverrides,
    setConnectionOverride,
    highlightAppId,
    handleConnectApp,
    handleUpdateSmsChatConnection,
    dismissHighlight,
    resolveCommunicationPreferencesConnection,
  } = useConnectedAppsLogic({
    highlightAppId: highlightAppIdProp,
  });

  if (connectedAppsQuery.isError) {
    return (
      <AppsContainer size={size}>
        <ErrorMessage>Error loading connected apps</ErrorMessage>
      </AppsContainer>
    );
  }

  const resolvedApps = connectedApps.map((app) => ({
    ...app,
    connection: connectionOverrides[app.id] ?? app.connection,
  }));

  return (
    <>
      {highlightAppId && <Overlay onClick={dismissHighlight} />}
      <DisabledOverlay disabled={actionsAppsDisconnectMutation.isPending}>
        <AppsContainer size={size}>
          {connectedAppsQuery.isLoading && (
            <>
              {[1, 2].map((_, i) => (
                <AppConnectionCard size={size} key={`skeleton-${i}`}>
                  <div>
                    <CircularProgress size={24} />
                    <Skeleton width={80} height={36} />
                  </div>
                  <Skeleton width={140} />
                </AppConnectionCard>
              ))}
            </>
          )}

          {!connectedAppsQuery.isLoading &&
            resolvedApps.map((app) => {
              const connectedSibling = getConnectedSibling(
                resolvedApps,
                app.id,
              );
              const siblingIsConnecting =
                connectedSibling?.connection === "connecting";

              return (
                <AppConnection
                  key={`${app.id}-${app.connection}`}
                  app={{
                    id: app.id,
                    connection: app.connection,
                    name: app.name,
                    description: app.description,
                  }}
                  handleConnectApp={handleConnectApp}
                  setConnection={(connection) => {
                    setConnectionOverride(app.id!, connection);
                  }}
                  size={size}
                  highlighted={highlightAppId === app.id}
                  conflictingApp={
                    siblingIsConnecting ? undefined : connectedSibling
                  }
                  onResolveConflict={(conflictingAppId) =>
                    handleConnectApp({ id: conflictingAppId, connect: false })
                  }
                  disabled={
                    siblingIsConnecting && app.connection !== "connected"
                  }
                  disabledTooltip={
                    siblingIsConnecting
                      ? `Please wait for ${connectedSibling!.name} to finish connecting.`
                      : undefined
                  }
                />
              );
            })}

          {!connectedAppsQuery.isLoading && (
            <RoleAuthorized
              userRoles={user.roles}
              authorizedRoles={[UserRoles.User]}
              authorizedComponent={
                <AppConnection
                  key={`smsChat-${user.communicationPreferences.smsChat?.isEnabled ? "connected" : "not-connected"}`}
                  app={{
                    id: "smsChat",
                    connection:
                      resolveCommunicationPreferencesConnection("smsChat"),
                    name: "Chat via SMS",
                    description: user.phoneNumber
                      ? `Use your mobile number ${formatInternationalPhoneNumber(user.phoneNumber)} to chat with Sidekick by text message.`
                      : "Add your mobile number to your profile to start chatting with Sidekick by text message.",
                  }}
                  handleConnectApp={handleUpdateSmsChatConnection}
                  setConnection={(connection) => {
                    setConnectionOverride("smsChat", connection);
                  }}
                  size={size}
                />
              }
            />
          )}

          {!connectedAppsQuery.isLoading &&
            (connectedAppsQuery.data?.length ?? 0) === 0 && (
              <Typography variant="body2" paddingTop={1}>
                No apps available at the moment.
              </Typography>
            )}
        </AppsContainer>
      </DisabledOverlay>
    </>
  );
};
